<template>
  <comparison-graph :date-range="dateRange" :biz="biz" :top-performer="topPerformer" api-endpoint="star-rating-chart-breakdown">
    <table class="table is-fullwidth summary-table">
      <thead>
      <tr>
        <td class="pad-top-22">Business</td>
        <td class="pad-top-22 w116">Average star rating</td>
        <td class="pad-top-22">Reviews</td>
        <td class="has-text-centered width-137 rating-column">
          <rating :stars="1" class="font-size-5 has-width-auto"></rating>
          1 Star
        </td>
        <td class="has-text-centered width-137 rating-column">
          <rating :stars="2" class="font-size-5 has-width-auto"></rating>
          2 Star
        </td>
        <td class="has-text-centered width-137 rating-column">
          <rating :stars="3" class="font-size-5 has-width-auto"></rating>
          3 Star
        </td>
        <td class="has-text-centered width-137 rating-column">
          <rating :stars="4" class="font-size-5 has-width-auto"></rating>
          4 Star
        </td>
        <td class="has-text-centered width-137 rating-column">
          <rating :stars="5" class="font-size-5 has-width-auto"></rating>
          5 Star
        </td>
      </tr>
      </thead>
      <tbody>
      <tr v-if="!isBreakDownDataReady">
        <td colspan="8" class="has-text-centered">
          <img alt="Fetching chart data..." class="small-spinner" src="/static/img/spinner.svg"/>
        </td>
      </tr>
      <template v-else>
        <tr v-for="record in breakDownData">
          <td>{{ record.name }}</td>
          <td>
            <div class="is-flex flex-align-center avg-rating">
              <rating :stars="record.averageRating"
                      :is-avg="true"
                      class="margin-right-5"></rating>
              {{ record.averageRating }}
            </div>
          </td>
          <td>{{ record.totalReviews }}</td>
          <td>
            <div class="is-flex">
              <progress class="progress custom-progress is-one margin-right-5 margin-top-3"
                        :value="record['1_star_percent']" max="100"/>
              {{ record['1_star_percent'] }}%
            </div>
          </td>
          <td>
            <div class="is-flex">
              <progress class="progress custom-progress is-two margin-right-5 margin-top-3"
                        :value="record['2_star_percent']" max="100"/>
              {{ record['2_star_percent'] }}%
            </div>
          </td>
          <td>
            <div class="is-flex">
              <progress class="progress custom-progress is-three margin-right-5 margin-top-3"
                        :value="record['3_star_percent']" max="100"/>
              {{ record['3_star_percent'] }}%
            </div>
          </td>
          <td>
            <div class="is-flex">
              <progress class="progress custom-progress is-four margin-right-5 margin-top-3"
                        :value="record['4_star_percent']" max="100"/>
              {{ record['4_star_percent'] }}%
            </div>
          </td>
          <td>
            <div class="is-flex">
              <progress class="progress custom-progress is-five margin-right-5 margin-top-3"
                        :value="record['5_star_percent']" max="100"/>
              {{ record['5_star_percent'] }}%
            </div>
          </td>
        </tr>
      </template>
      </tbody>
    </table>
  </comparison-graph>
</template>

<script>
import ComparisonGraph from './ComparisonGraph'
import {Rating} from '@/components'
import axios from 'axios'
import {misc} from '@/mixins'

export default {
  name: 'RatingsCompare',
  components: {ComparisonGraph, Rating},
  mixins: [misc],
  props: {
    dateRange: {
      type: Object,
      required: true
    },
    biz: {
      type: Array,
      required: true
    },
    summary: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      isBreakDownDataReady: false,
      breakDownData: []
    }
  },
  computed: {
    topPerformer () {
      const data = {name: '', score: ''}

      if (this.breakDownData.length) {
        const max = this.breakDownData.reduce((prev, current) => {
          return (!current.all_star || parseFloat(prev.averageRating) > parseFloat(current.averageRating)) ? prev : current
        })
        data.name = max.name
        data.score = max.averageRating
      }

      return data
    }
  },
  created () {
    this.fetchRatingBreakdown()
  },
  methods: {
    /**
     * Fetch the data for comparison table
     *
     * @returns {void}
     */
    fetchRatingBreakdown () {
      this.isBreakDownDataReady = false
      this.breakDownData = []

      if (this.biz.length && this.summary.length) {
        const jsonBody = {
          dates: this.dateRange,
          businesses: this.biz
        }
        axios.post(`/competitors/star-rating-breakdown`, jsonBody)
          .then(({data}) => {
            data.forEach((d, i) => {
              d['1_star_percent'] = this.calcProgressPercent(d['1_star'], d['all_star'])
              d['2_star_percent'] = this.calcProgressPercent(d['2_star'], d['all_star'])
              d['3_star_percent'] = this.calcProgressPercent(d['3_star'], d['all_star'])
              d['4_star_percent'] = this.calcProgressPercent(d['4_star'], d['all_star'])
              d['5_star_percent'] = this.calcProgressPercent(d['5_star'], d['all_star'])

              const summaryData = this.summary.find(business => business.business_id === d['id'])
              if (summaryData) {
                d['totalReviews'] = summaryData.total_reviews
                d['averageRating'] = summaryData.average_rating
              }
            })

            this.breakDownData = data
            this.isBreakDownDataReady = true
          })
          .catch(() => {
            window.events.$emit('flash-error', {
              group: 'competitor-report',
              msg: 'There was an error retrieving the Star Rating data, please try again later',
              gtmName: 'Failed fetching Star Rating data on Competitors Index View'
            })
          })
      } else {
        this.isBreakDownDataReady = true
      }
    }
  },
  watch: {
    dateRange () {
      this.fetchRatingBreakdown()
    },
    biz () {
      this.fetchRatingBreakdown()
    },
    summary () {
      this.fetchRatingBreakdown()
    }
  }
}
</script>

<style scoped>
.summary-table {
  background-color: #fafafa;
  margin-top: 30px;
}

.summary-table.table tbody tr {
  background-color: #fafafa !important;
}

.pad-top-22 {
  padding-top: 22px !important;
}

.margin-left--3 {
  margin-left: -3px !important;
}

.width-137 {
  width: 137px;
}

.progress {
  width: 90px;
}

.w116 {
  width: 116px;
}

.summary-table .avg-rating {
  align-items: center;
}

.summary-table .avg-rating .has-width-auto >>> img {
  width: 60px;
}

.summary-table .rating-column .has-width-auto >>> img {
  height: 8px;
}

.avg-rating >>> img {
  height: 12px;
  margin-top: -3px;
}
</style>



// WEBPACK FOOTER //
// src/pages/analytics/competitors/RatingsCompare.vue